/**
 * Duplicação de documentos — recibo, ordem de serviço e contrato.
 *
 * O orçamento já duplicava (individual e em massa, na `QuotesListPage`). Estes
 * três faltavam, e o que torna a feature perigosa não é o que se COPIA: é o que
 * NÃO se pode copiar. Cada documento carrega campos que registram um FATO
 * ocorrido — uma assinatura, um cancelamento, um pagamento, um vínculo de
 * origem —, e um fato copiado vira um fato falso que ninguém percebe, porque a
 * cópia parece perfeitamente normal na tela.
 *
 * Por isso cada builder aqui é uma LISTA EXPLÍCITA: campo novo na tabela não
 * entra na cópia por acidente, e o que é deliberadamente descartado está
 * escrito com o motivo ao lado. As funções são puras e devolvem o INPUT das
 * mutations que já existem (`useReceipts`/`useServiceOrders`/`useContracts`) —
 * assim a cópia herda de graça a numeração da sequência, o `company_id`, o
 * gate de papel e a fila offline, em vez de reimplementar tudo.
 */

/** Sufixo do título/nome da cópia — o mesmo que o orçamento já usa. */
export const COPY_SUFFIX = '(Cópia)';

export const withCopySuffix = (value: string | null | undefined, fallback: string): string => {
  const base = (value ?? '').trim();
  return base ? `${base} ${COPY_SUFFIX}` : `${fallback} ${COPY_SUFFIX}`;
};

type Row = Record<string, unknown>;

const str = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const t = String(v).trim();
  return t.length ? t : null;
};
const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ---------------------------------------------------------------------------
// RECIBO
// ---------------------------------------------------------------------------
export interface ReceiptDuplicateOptions {
  /** Data de emissão da cópia, 'YYYY-MM-DD' no calendário LOCAL. */
  todayIso: string;
}

/**
 * Recibo é comprovante de um pagamento que ACONTECEU. Duplicar faz sentido no
 * caso recorrente (aluguel, mensalidade): mesmo pagador, mesmo valor, mês novo.
 *
 * O que NÃO é copiado, e por quê:
 *
 *  • `quote_id` / `service_order_id` / `finance_entry_id` — amarram o recibo a
 *    UM documento e a UM lançamento. Copiados, dois recibos passariam a
 *    reivindicar o mesmo pagamento, e quitar o lançamento uma vez daria baixa
 *    "duas vezes" aos olhos de quem lê a tela.
 *  • `cancelled_at` — a cópia de um recibo cancelado nasceria cancelada, que é
 *    o oposto do que se quer ao duplicar.
 *  • `issue_date` — vira HOJE. Um recibo é datado do dia em que se recebeu;
 *    herdar a data do original faria a cópia mentir sobre quando o dinheiro
 *    entrou, e é justamente por data que o relatório de pagamentos agrupa.
 *
 * O que É copiado e talvez surpreenda: `issuer_signature`. É a assinatura do
 * PRÓPRIO emitente, salva por ele para reusar em todos os documentos — não é o
 * registro de um ato de terceiro. Copiá-la é o comportamento desejado.
 */
export function buildReceiptDuplicate(source: Row, opts: ReceiptDuplicateOptions) {
  return {
    client_id: (str(source.client_id) ?? null) as string | null,
    payer_name: withCopySuffix(str(source.payer_name), 'Recibo'),
    payer_document: str(source.payer_document),
    payer_address: str(source.payer_address),
    amount: num(source.amount),
    description: str(source.description),
    payment_method: str(source.payment_method),
    issue_place: str(source.issue_place),
    issuer_signature: str(source.issuer_signature),
    issuer_signer_name: str(source.issuer_signer_name),
    notes: str(source.notes),
    show_sequence_id: source.show_sequence_id === true,

    issue_date: opts.todayIso,
    // Explícitos, e não omitidos: um leitor deste código precisa VER que o
    // descarte foi decidido, não deduzir de uma ausência.
    quote_id: null,
    service_order_id: null,
    finance_entry_id: null,
    cancelled_at: null,
  };
}

// ---------------------------------------------------------------------------
// ORDEM DE SERVIÇO
// ---------------------------------------------------------------------------
/**
 * O que NÃO é copiado, e por quê:
 *
 *  • **`quote_id` — o mais perigoso da feature.** Uma OS que aponta para um
 *    orçamento é PULADA no CMV do DRE, porque o custo dela já foi contado no
 *    orçamento de origem (é a regra que evita dupla contagem). Uma cópia
 *    carregando esse vínculo ficaria fora do custo PARA SEMPRE: o lucro
 *    apareceria maior do que é, sem erro, sem aviso e sem nada na tela que
 *    denuncie. A cópia é um trabalho NOVO e tem que custar.
 *  • `diagnosis` / `solution` — são as CONCLUSÕES daquele reparo ("capacitor
 *    estufado", "troquei a fonte"). Copiadas, a OS nova nasceria afirmando um
 *    laudo que ninguém fez, num documento que o cliente recebe.
 *  • `status` / `completed_at` — a cópia começa em `pending`. Nascer
 *    "concluída" dispararia baixa de estoque e entraria no CMV de um serviço
 *    que não foi executado.
 *  • `scheduled_date` — a cópia não está agendada; herdar a data colocaria na
 *    Agenda um compromisso que ninguém marcou.
 *
 * `defect` É copiado: é o relato do CLIENTE ("não liga"), que no serviço
 * recorrente costuma ser o mesmo e serve de ponto de partida — diferente do
 * diagnóstico, ele não afirma nada sobre o que foi encontrado.
 *
 * Os blocos novos seguem a mesma régua:
 *  • **equipamento** (`asset_*`) É copiado — manutenção recorrente é no MESMO
 *    aparelho, e redigitar número de série é onde se erra;
 *  • **desconto/acréscimo, pagamento, garantia e termos** SÃO copiados: são as
 *    condições comerciais do prestador, não conclusões daquele reparo;
 *  • **`expected_date`** não: prazo prometido é daquele atendimento, como o
 *    agendamento;
 *  • **a assinatura do cliente** JAMAIS. É a prova de que alguém recebeu um
 *    serviço; herdada, a cópia nasceria afirmando uma entrega que não houve —
 *    o mesmo raciocínio que proíbe copiar assinatura de contrato.
 */
export function buildServiceOrderDuplicate(source: Row, items: Row[], photos: Row[] = []) {
  return {
    client_id: (str(source.client_id) ?? null) as string | null,
    client_name: str(source.client_name),
    client_phone: str(source.client_phone),
    client_address: str(source.client_address),
    client_document: str(source.client_document),
    client_email: str(source.client_email),
    title: withCopySuffix(str(source.title), 'Ordem de serviço'),
    description: str(source.description),
    total: num(source.total),
    discount: num(source.discount),
    discount_type: (str(source.discount_type) ?? 'value') as 'value' | 'percent',
    shipping: num(source.shipping),
    show_sequence_id: source.show_sequence_id === true,
    defect: str(source.defect),

    asset_type: str(source.asset_type),
    asset_brand: str(source.asset_brand),
    asset_identifier: str(source.asset_identifier),
    asset_accessories: str(source.asset_accessories),
    asset_condition: str(source.asset_condition),

    attendant_name: str(source.attendant_name),
    technician_name: str(source.technician_name),
    payment_methods: Array.isArray(source.payment_methods)
      ? (source.payment_methods as string[])
      : null,
    payment_terms: str(source.payment_terms),
    warranty_amount: source.warranty_amount == null ? null : num(source.warranty_amount),
    warranty_unit: str(source.warranty_unit),
    terms_conditions: str(source.terms_conditions),

    quote_id: null,
    status: 'pending' as const,
    scheduled_date: null,
    expected_date: null,
    completed_at: null,
    diagnosis: null,
    solution: null,
    client_signature: null,
    client_signer_name: null,
    client_signed_at: null,

    items: items.map((item, index) => ({
      product_id: (str(item.product_id) ?? null) as string | null,
      name: str(item.name) ?? '',
      description: str(item.description),
      quantity: num(item.quantity ?? 1),
      unit_price: num(item.unit_price),
      discount: num(item.discount),
      discount_type: (str(item.discount_type) ?? 'value') as 'value' | 'percent',
      unit: str(item.unit),
      image_url: str(item.image_url),
      // O SNAPSHOT de custo vem junto, mesma escolha que a duplicação de
      // orçamento faz: a cópia é uma cópia do documento, economia inclusa. Quem
      // quiser repreçar edita — e um `null` aqui apagaria o custo histórico que
      // o original tinha, que seria pior.
      unit_cost: item.unit_cost === null || item.unit_cost === undefined ? null : num(item.unit_cost),
      bundle_parent_product_id: (str(item.bundle_parent_product_id) ?? null) as string | null,
      position: index,
    })),
    photos: photos.map((photo, index) => ({
      image_url: str(photo.image_url) ?? str(photo.imageUrl),
      title: str(photo.title),
      description: str(photo.description),
      position: index,
    })),
  };
}

// ---------------------------------------------------------------------------
// CONTRATO
// ---------------------------------------------------------------------------
/**
 * Contrato é o caso em que copiar demais não é bug, é FRAUDE: as duas
 * assinaturas, as datas de assinatura, o IP e o user-agent do signatário e o
 * SHA-256 do conteúdo assinado formam a trilha de auditoria de um ato jurídico.
 * Uma cópia que os herdasse afirmaria que alguém assinou um documento que nunca
 * viu — e o PDF sairia com a assinatura desenhada, com data e com hash.
 *
 * A defesa principal é ESTRUTURAL, não deste arquivo: `ContractInput` (o input
 * da mutation de criação) sequer expõe os campos de assinatura, então não há
 * como um contrato nascer assinado por esse caminho. Este builder existe para
 * a metade que o tipo não protege — `status`, que precisa voltar a `draft`
 * (o corpo do contrato é congelado fora de `draft`, então uma cópia em
 * `signed` nasceria imutável e inútil).
 *
 * `public_token`, `sent_at`, `expires_at` e `cpf_attempts` também não passam:
 * o token é a credencial de acesso do link público de assinatura, e dois
 * contratos com o mesmo token deixariam o segundo abrir o primeiro.
 */
export function buildContractDuplicate(source: Row) {
  return {
    client_id: (str(source.client_id) ?? null) as string | null,
    client_name: str(source.client_name),
    client_phone: str(source.client_phone),
    client_address: str(source.client_address),
    client_document: str(source.client_document),
    title: withCopySuffix(str(source.title), 'Contrato'),
    content: str(source.content),
    total: num(source.total),
    show_sequence_id: source.show_sequence_id === true,

    // A cópia é um rascunho novo: editável, sem token, sem trilha.
    status: 'draft' as const,
    quote_id: null,
  };
}
